/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, CheckCircle2, ArrowLeft, Loader2, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { useOnboardingStatus } from '@/lib/onboardingStatus';
import { StepIndicator } from '@/components/onboarding/tiers/parts';
import { Tier1Step } from '@/components/onboarding/tiers/Tier1Step';
import { Tier2Step } from '@/components/onboarding/tiers/Tier2Step';
import { Tier3Step } from '@/components/onboarding/tiers/Tier3Step';

/**
 * Progressive Onboarding — 3 Tier wizard for medici / infermieri / OSS.
 * Renders one step at a time based on current server-side onboarding status.
 * I singoli step vivono in `components/onboarding/tiers/*`.
 */

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
const OnboardingPage = () => {
  const { user, refreshUser } = useAuth();
  const { status, loading, refresh: refreshSharedStatus } = useOnboardingStatus();
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    // After a mutation, refresh both the auth user (to pick up new fields) and the
    // shared onboarding context (which re-renders pill + banner + this page).
    await Promise.all([
      refreshSharedStatus(),
      refreshUser && refreshUser(),
    ]);
  }, [refreshSharedStatus, refreshUser]);

  const isPatient = user?.role === 'patient' || user?.category === 'paziente';

  const completed = useMemo(() => {
    const arr = [];
    if (status?.tier1_completed) arr.push('tier1');
    if (status?.tier2_completed) arr.push('tier2');
    if (status?.tier3_completed) arr.push('tier3');
    return arr;
  }, [status]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isPatient) {
    return (
      <Card className="p-8 max-w-2xl mx-auto text-center" data-testid="onboarding-not-professional">
        <Info className="h-8 w-8 text-primary mx-auto mb-3" />
        <h2 className="font-display font-bold text-xl mb-2">Onboarding not required</h2>
        <p className="text-muted-foreground">This tiered onboarding applies only to healthcare professionals.</p>
        <Button className="mt-4" onClick={() => navigate('/feed')}>Back to feed</Button>
      </Card>
    );
  }

  const current = status?.next_step || 'tier1';

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl md:text-3xl" data-testid="onboarding-title">
            Professional onboarding
          </h1>
          <p className="text-sm text-muted-foreground">
            Complete each tier to unlock features. Progress is saved automatically.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/feed')} data-testid="onboarding-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Later
        </Button>
      </div>

      <StepIndicator current={current} completed={completed} />

      <Card className="p-5 md:p-6">
        {current === 'tier1' && <Tier1Step user={user} onDone={refresh} />}
        {(current === 'tier2' || current === 'tier2_awaiting_review') && (
          <Tier2Step user={user} status={status} onDone={refresh} />
        )}
        {current === 'tier3' && <Tier3Step user={user} onDone={refresh} />}
        {!current && (
          <div className="text-center py-8" data-testid="onboarding-complete">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h2 className="font-display font-bold text-xl">All tiers complete!</h2>
            <p className="text-muted-foreground mt-2">Your professional profile is fully activated.</p>
            <Button className="mt-4" onClick={() => navigate('/feed')}>Go to dashboard</Button>
          </div>
        )}
      </Card>

      {status?.can_book_patients && (
        <div className="mt-4 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
          data-testid="onboarding-bookable-badge">
          <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
            <ShieldCheck className="h-4 w-4" />
            You are now verified and visible in the patient-facing directory.
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingPage;
